import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    ChannelMixColors,
    dividePercent,
    formatAxisTooltip,
    formatChartNumber,
    KpiCard,
    Panel,
    ReportingPageDescription,
} from "./ReportingLab.jsx";

function statusTone(value, warnThreshold, badThreshold) {
    if (value === null || value === undefined) {
        return "default";
    }

    if (value < badThreshold) {
        return "bad";
    }

    if (value < warnThreshold) {
        return "warn";
    }

    return "good";
}

function formatTrackingDate(value) {
    if (value instanceof Date) {
        return value.toLocaleDateString("cs-CZ");
    }

    if (typeof value === "string") {
        return value;
    }

    return value ? String(value) : "-";
}

export default function ReportingTrackingQualityLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setData(null);

        const erpApi = new ErpCalculationApi(getAuthorityConfiguration());

        erpApi.trackingQuality({projectId: project.id, erpFilter})
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setData(nextData);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, erpFilter]);

    const dailyRows = data?.daily ?? [];
    const domainRows = [...(data?.domains ?? [])].sort((a, b) => b.orderEvents - a.orderEvents);
    const orderPairingRate = dividePercent(data?.pairedOrderEvents ?? 0, data?.orderEvents ?? 0);
    const erpCoverageRate = dividePercent(data?.erpOrdersWithPageOrderEvent ?? 0, data?.erpOrders ?? 0);
    const unpairedOrderEvents = data?.unpairedOrderEvents ?? 0;
    const orderEventsWithoutOrderNumber = data?.orderEventsWithoutOrderNumber ?? 0;

    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const dailyOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 24, bottom: 46, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "cross"},
            formatter: (params) => formatAxisTooltip(params, formatChartNumber),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: dailyRows.map((row) => formatTrackingDate(row.date)),
            axisLabel: {fontSize: 12},
        },
        yAxis: {type: "value", axisLabel: {fontSize: 12, formatter: formatChartNumber}},
        series: [
            {
                name: t("reporting_lab.tracking.paired_order_events", {defaultValue: "Spárované order eventy"}),
                type: "bar",
                stack: "orderEvents",
                data: dailyRows.map((row) => row.pairedOrderEvents),
                barMaxWidth: 22,
                itemStyle: {color: "#16a34a"},
            },
            {
                name: t("reporting_lab.tracking.unpaired_order_events_short", {defaultValue: "Nespárované"}),
                type: "bar",
                stack: "orderEvents",
                data: dailyRows.map((row) => Math.max(0, row.orderEvents - row.pairedOrderEvents)),
                barMaxWidth: 22,
                itemStyle: {color: "#e11d48"},
            },
            {
                name: t("reporting_lab.tracking.order_events_total", {defaultValue: "Order eventy celkem"}),
                type: "line",
                smooth: true,
                data: dailyRows.map((row) => row.orderEvents),
                symbolSize: 5,
                lineStyle: {width: 2},
                itemStyle: {color: "#2563eb"},
            },
            {
                name: t("reporting_lab.tracking.erp_orders_daily", {defaultValue: "ERP objednávky"}),
                type: "line",
                smooth: true,
                data: dailyRows.map((row) => row.erpOrders ?? 0),
                symbolSize: 5,
                itemStyle: {color: "#f97316"},
            },
        ],
    };

    const domainOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 18, bottom: 42, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, formatChartNumber),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...domainRows].reverse().slice(0, 10).map((row) => row.domainName),
            axisLabel: {fontSize: 12, width: 220, overflow: "truncate"},
        },
        series: [
            {
                name: t("reporting_lab.tracking.order_events", {defaultValue: "Order eventy"}),
                type: "bar",
                data: [...domainRows].reverse().slice(0, 10).map((row) => row.orderEvents),
                barMaxWidth: 22,
            },
            {
                name: t("reporting_lab.tracking.paired_order_events", {defaultValue: "Spárované"}),
                type: "bar",
                data: [...domainRows].reverse().slice(0, 10).map((row) => row.pairedOrderEvents),
                barMaxWidth: 22,
            },
        ],
    };

    return (
        <WideSecureLayout periodReport retentionExpired={retentionExpired}
            title={t("reporting_lab.pages.tracking_quality", {defaultValue: "Měření a párování"})}
            actions={<OrderDashboardFilters project={project} showModeSwitch={false}/>}
         intro={<ReportingPageDescription
                    title={t("reporting_lab.descriptions.tracking_quality.title", {defaultValue: "K čemu slouží měření a párování"})}
                >
                    {t("reporting_lab.descriptions.tracking_quality.text", {defaultValue: "Přehled kontroluje order eventy: jejich spárování s ERP objednávkami, chybějící čísla objednávek a správnost domén. Návštěvnost a další webové metriky nejsou součástí tohoto reportu."})}
                </ReportingPageDescription>}>
            <div className="space-y-4">

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                    <KpiCard loading={loading}
                        label={t("reporting_lab.tracking.order_events", {defaultValue: "Order eventy"})}
                        value={renderNumber(data?.orderEvents ?? 0)}
                        helper={t("reporting_lab.tracking.order_events_help", {defaultValue: "Order eventy přijaté v daném období"})}
                    />
                    <KpiCard loading={loading}
                        label={t("reporting_lab.tracking.order_pairing_rate", {defaultValue: "Spárovanost order eventů"})}
                        value={renderPercentValue(orderPairingRate)}
                        helper={`${renderNumber(data?.pairedOrderEvents ?? 0)} / ${renderNumber(data?.orderEvents ?? 0)}`}
                        tone={statusTone(orderPairingRate, 95, 85)}
                    />
                    <KpiCard loading={loading}
                        label={t("reporting_lab.tracking.erp_order_event_coverage", {defaultValue: "Pokrytí ERP objednávek"})}
                        value={renderPercentValue(erpCoverageRate)}
                        helper={`${renderNumber(data?.erpOrdersWithPageOrderEvent ?? 0)} / ${renderNumber(data?.erpOrders ?? 0)}`}
                        tone={statusTone(erpCoverageRate, 90, 75)}
                    />
                    <KpiCard loading={loading}
                        label={t("reporting_lab.tracking.order_events_without_number", {defaultValue: "Bez čísla objednávky"})}
                        value={renderNumber(orderEventsWithoutOrderNumber)}
                        helper={t("reporting_lab.tracking.order_events_without_number_help", {defaultValue: "Order event nelze bez čísla spojit s ERP objednávkou"})}
                        tone={orderEventsWithoutOrderNumber > 0 ? "bad" : "good"}
                    />
                    <KpiCard loading={loading}
                        label={t("reporting_lab.tracking.unpaired_order_events", {defaultValue: "Nespárované order eventy"})}
                        value={renderNumber(unpairedOrderEvents)}
                        helper={t("reporting_lab.tracking.unpaired_order_events_help", {defaultValue: "Order eventy čekající na ERP objednávku nebo neúspěšné párování"})}
                        tone={unpairedOrderEvents > 0 ? "warn" : "good"}
                    />
                </div>

                <Panel loading={loading} title={t("reporting_lab.tracking.events_in_time", {defaultValue: "Order eventy a objednávky v čase"})}>
                    <EChart option={dailyOption} height={380}/>
                </Panel>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <Panel loading={loading} title={t("reporting_lab.tracking.events_by_domain", {defaultValue: "Order eventy podle domén"})}>
                        <EChart option={domainOption} height={380}/>
                    </Panel>
                    <Panel loading={loading} title={t("reporting_lab.tracking.domain_table", {defaultValue: "Doménový detail"})}>
                        <div className="overflow-x-auto">
                            <table className="table-auto w-full text-sm">
                                <thead className="text-xs uppercase text-slate-500 bg-slate-50 dark:bg-slate-800/60">
                                <tr>
                                    <th className="px-4 py-3 text-left">{t("reporting_lab.data_quality.domain", {defaultValue: "Doména"})}</th>
                                    <th className="px-4 py-3 text-right">{t("reporting_lab.tracking.order_events", {defaultValue: "Order eventy"})}</th>
                                    <th className="px-4 py-3 text-right">{t("reporting_lab.tracking.paired_order_events", {defaultValue: "Spárované"})}</th>
                                    <th className="px-4 py-3 text-right">{t("reporting_lab.tracking.unpaired_order_events_short", {defaultValue: "Nespárované"})}</th>
                                    <th className="px-4 py-3 text-right">{t("reporting_lab.tracking.pairing_rate", {defaultValue: "Spárování"})}</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {domainRows.map((row) => {
                                    const pairingRate = dividePercent(row.pairedOrderEvents, row.orderEvents);

                                    return <tr key={`${row.domainName}-${row.domainEnabled}`}>
                                        <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">
                                            <div className="flex items-center gap-2">
                                                <span>{row.domainName}</span>
                                                {row.domainEnabled === false && <span className="rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">{t("reporting_lab.data_quality.disabled", {defaultValue: "Vypnutá"})}</span>}
                                                {row.domainEnabled === null && <span className="rounded border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-semibold text-datixo-negative">{t("reporting_lab.tracking.unknown_domain", {defaultValue: "Mimo projekt"})}</span>}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right text-slate-500">{renderNumber(row.orderEvents)}</td>
                                        <td className="px-4 py-3 text-right text-slate-500">{renderNumber(row.pairedOrderEvents)}</td>
                                        <td className="px-4 py-3 text-right text-slate-500">{renderNumber(Math.max(0, row.orderEvents - row.pairedOrderEvents))}</td>
                                        <td className="px-4 py-3 text-right text-slate-500">{renderPercentValue(pairingRate)}</td>
                                    </tr>;
                                })}
                                </tbody>
                            </table>
                        </div>
                    </Panel>
                </div>

                <Panel loading={loading} title={t("reporting_lab.tracking.daily_table", {defaultValue: "Denní detail měření"})}>
                    <div className="overflow-x-auto">
                        <table className="table-auto w-full text-sm">
                            <thead className="text-xs uppercase text-slate-500 bg-slate-50 dark:bg-slate-800/60">
                            <tr>
                                <th className="px-4 py-3 text-left">{t("common.date", {defaultValue: "Datum"})}</th>
                                <th className="px-4 py-3 text-right">{t("reporting_lab.tracking.order_events", {defaultValue: "Order eventy"})}</th>
                                <th className="px-4 py-3 text-right">{t("reporting_lab.tracking.paired_order_events", {defaultValue: "Spárované"})}</th>
                                <th className="px-4 py-3 text-right">{t("reporting_lab.tracking.unpaired_order_events_short", {defaultValue: "Nespárované"})}</th>
                                <th className="px-4 py-3 text-right">{t("reporting_lab.tracking.pairing_rate", {defaultValue: "Spárování"})}</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {dailyRows.map((row) => {
                                const pairingRate = dividePercent(row.pairedOrderEvents, row.orderEvents);

                                return <tr key={formatTrackingDate(row.date)}>
                                    <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{formatTrackingDate(row.date)}</td>
                                    <td className="px-4 py-3 text-right text-slate-500">{renderNumber(row.orderEvents)}</td>
                                    <td className="px-4 py-3 text-right text-slate-500">{renderNumber(row.pairedOrderEvents)}</td>
                                    <td className="px-4 py-3 text-right text-slate-500">{renderNumber(Math.max(0, row.orderEvents - row.pairedOrderEvents))}</td>
                                    <td className="px-4 py-3 text-right text-slate-500">{renderPercentValue(pairingRate)}</td>
                                </tr>;
                            })}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>
        </WideSecureLayout>
    );
}
